import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { moneySchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { marketLedgerStatusSchema, positiveQuantitySchema, stockTransferStatusSchema } from "./enums.js";
/**
 * İstasyonlar (depolar) arası transfer (Prompt 16, ADR 0016 §11). Yalnızca AYNI şirketin depoları arasında: farklı
 * şirkete mal çıkışı satış faturasıdır. Gönderen istasyon gönderir (`TRANSFER_OUT`, o anki ortalamayla), alan
 * istasyon kabul eder (`TRANSFER_IN`, gönderilen birim maliyetle; eksik gelen fire). Fiş kabulde kesilir.
 * Tutarlılık: Σ gönderilen değer = Σ alınan değer + eksik değeri.
 */
export const stockTransferLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    lotId: uuidSchema.nullable().describe("Belirli partiden gönderim (boşsa FEFO)"),
    lotNo: z.string().nullable(),
    sentQuantity: quantitySchema,
    receivedQuantity: quantitySchema.nullable(),
    shortQuantity: quantitySchema.nullable(),
    sentValue: moneySchema,
    receivedValue: moneySchema,
    shortValue: moneySchema,
    shortNote: z.string().nullable(),
});
export const stockTransferSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    transferNo: z.string(),
    fromStationId: uuidSchema,
    fromStationName: z.string(),
    fromWarehouseId: uuidSchema,
    fromWarehouseCode: z.string(),
    toStationId: uuidSchema,
    toStationName: z.string(),
    toWarehouseId: uuidSchema,
    toWarehouseCode: z.string(),
    status: stockTransferStatusSchema,
    note: z.string().nullable(),
    sentAt: timestampSchema.nullable(),
    sentBy: uuidSchema.nullable(),
    receivedAt: timestampSchema.nullable(),
    receivedBy: uuidSchema.nullable(),
    sentValue: moneySchema,
    receivedValue: moneySchema,
    shortValue: moneySchema.describe("Eksik gelenin değeri (olağandışı kayıp)"),
    journalEntryId: uuidSchema.nullable(),
    ledgerStatus: marketLedgerStatusSchema,
    lineCount: z.number().int(),
    lines: z.array(stockTransferLineSchema).describe("Liste ucunda boş döner; ayrıntı ucunda dolu"),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const stockTransferPageSchema = pageOf(stockTransferSchema);
export const listStockTransfersQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional().describe("Gönderen YA DA alan istasyon"),
    direction: z.enum(["OUTGOING", "INCOMING"]).optional().describe("`stationId` ile: yalnızca giden / gelen"),
    status: stockTransferStatusSchema.optional(),
});
const transferLinesInput = z
    .array(z.object({
    productId: uuidSchema,
    quantity: positiveQuantitySchema,
    lotId: uuidSchema.nullable().default(null).describe("Belirli partiden gönder (SKT takipli ürün)"),
}))
    .min(1)
    .max(500);
export const createStockTransferSchema = z.object({
    fromWarehouseId: uuidSchema,
    toWarehouseId: uuidSchema,
    note: z.string().trim().max(1000).nullable().default(null),
    lines: transferLinesInput,
});
/** TASLAK transferi TAMAMEN değiştirir (not + satırlar). */
export const saveStockTransferSchema = z.object({
    note: z.string().trim().max(1000).nullable().default(null),
    lines: transferLinesInput,
    version: z.number().int().positive(),
});
export const sendStockTransferSchema = z.object({ version: z.number().int().positive() });
export const receiveStockTransferSchema = z.object({
    version: z.number().int().positive(),
    lines: z
        .array(z.object({
        lineId: uuidSchema,
        receivedQuantity: quantitySchema
            .refine((value) => Number(value) >= 0, "Miktar negatif olamaz")
            .describe("Gelen miktar (≤ gönderilen); eksik fire olur"),
        shortNote: z.string().trim().max(300).nullable().default(null),
    }))
        .max(500)
        .default([])
        .describe("Verilmeyen satır TAM gelmiş sayılır"),
});
export const cancelStockTransferSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3, "İptal nedeni yazın").max(300),
});
//# sourceMappingURL=transfers.js.map